// Shared compile context for multi-source runtime loading.
// Example: later `.luis` sources can reuse variables extracted from earlier ones.
import { compileLuis } from '@lentystyle/compiler'
import type { LuisCompileMode, LuisImportResolver } from '@lentystyle/compiler'
import { compileLuisWithIncrementalCache } from '@lentystyle/compiler/incremental'
import { getIncrementalCompileCache, setIncrementalCompileCache } from './context-incremental-cache'
import { buildContextPrelude, cloneArrayVariableMap, cloneObjectVariableMap } from './context-serialize'
import { formatDuration, getNow } from './context-time'
import { normalizeIncrementalStats } from './context-stats'
import { pushCompileRecoveryWarning, resolveCompileRecoverySource } from './process/compile-recovery'
import type { LuisCompileContext, LuisIncrementalPerf, LuisSourceElement } from './context-types'

export type { LuisArrayValue, LuisCompileContext, LuisIncrementalPerf, LuisObjectValue, LuisSourceElement } from './context-types'

// Create an empty cross-source context for a rebuild cycle.
export function createEmptyContext(): LuisCompileContext {
  // Return an empty context for a fresh compile cycle.
  return {
    classVariables: {},
    objectVariables: {},
    arrayVariables: {},
  }
}

// Clone compile context so cached snapshots can be reused safely.
export function cloneCompileContext(context: LuisCompileContext): LuisCompileContext {
  // Return a safe clone to avoid cross-cache mutations.
  return {
    classVariables: { ...context.classVariables },
    objectVariables: cloneObjectVariableMap(context.objectVariables),
    arrayVariables: cloneArrayVariableMap(context.arrayVariables),
  }
}

// Compile source with carried context so cross-source vars can resolve.
export function compileWithContext(
  source: string,
  context: LuisCompileContext,
  sourceId?: string,
  importResolver?: LuisImportResolver,
  importModeResolver?: (sourceId: string) => LuisCompileMode | undefined,
  compileMode?: LuisCompileMode,
  debugEnabled = false,
): { result: ReturnType<typeof compileLuis>; compileDurationMs: number; incremental?: LuisIncrementalPerf } {
  const prelude = buildContextPrelude(context)
  const sourceWithContext = prelude ? `${prelude}\n${source}` : source
  const lineOffset = countSourceLineOffset(prelude)
  const { result, compileDurationMs, incremental } = compileWithPerf(
    sourceWithContext,
    sourceId,
    importResolver,
    importModeResolver,
    compileMode,
    debugEnabled,
    lineOffset,
  )

  context.classVariables = { ...result.classVariables }
  context.objectVariables = { ...result.objectVariables }
  context.arrayVariables = { ...result.arrayVariables }

  // Return result and measured duration for diagnostics.
  return { result, compileDurationMs, incremental }
}

// Measure raw compile duration around one compile operation.
export function compileWithPerf(
  source: string,
  sourceId?: string,
  importResolver?: LuisImportResolver,
  importModeResolver?: (sourceId: string) => LuisCompileMode | undefined,
  compileMode?: LuisCompileMode,
  debugEnabled = false,
  lineOffset = 0,
): { result: ReturnType<typeof compileLuis>; compileDurationMs: number; incremental?: LuisIncrementalPerf } {
  const compileStart = getNow()
  const previousCache = getIncrementalCompileCache(sourceId)
  const compileOptions = {
    sourceId,
    importResolver,
    importModeResolver,
    compileMode,
    debug: debugEnabled,
    collectWarnings: debugEnabled,
    lineOffset,
  }
  let incrementalOutput

  try {
    // Return direct compile output when source compiles cleanly.
    incrementalOutput = compileLuisWithIncrementalCache(source, compileOptions, previousCache)
  } catch (error) {
    // Re-throw immediately when debug mode wants hard errors.
    if (debugEnabled) {
      throw error
    }
    const recovery = resolveCompileRecoverySource(source, error)
    // Re-throw when no supported recovery strategy exists.
    if (!recovery) {
      throw error
    }
    incrementalOutput = compileLuisWithIncrementalCache(recovery._source, compileOptions, previousCache)
    pushCompileRecoveryWarning(incrementalOutput.result.warnings, sourceId ?? '<inline>', recovery)
  }

  // Save next incremental cache snapshot by stable source id.
  if (sourceId) {
    setIncrementalCompileCache(sourceId, incrementalOutput.cache)
  } else {
    // Keep cache map unchanged when source id is unavailable.
  }

  const compileDurationMs = getNow() - compileStart
  const result = incrementalOutput.result
  const incremental: LuisIncrementalPerf = normalizeIncrementalStats(incrementalOutput.stats)

  // Return compile payload with elapsed time.
  return { result, compileDurationMs, incremental }
}

// Count prelude lines so debug source lines can map back to original source.
function countSourceLineOffset(prelude: string): number {
  // Return zero offset when no context prelude was prepended.
  if (!prelude) {
    // Return zero when no prelude text exists.
    return 0
  } else {
    // Continue source-line offset calculation.
  }

  // Return line count for prepended prelude text.
  return prelude.split('\n').length
}

export { formatDuration, getNow }
